import type {
  AudioVoiceoverRequest as Voiceover,
  CatalogAudioModel,
} from "../../../../api/generated/model";
import { AudioVoiceoverRequest } from "../../../../api/generated/zod";
import { voiceOf, type Cast } from "./cast";
import { usePricedStarts, type ActorStart } from "./use-priced-starts";

export interface VoiceoversOptions {
  teamId: string;
  brandId: string;
  /** The catalog's voiceover row (a8's `elevenlabs-v3-voiceover`), or none. */
  model: CatalogAudioModel | undefined;
  casts: readonly Cast[];
  script: string;
  /** The voiceover jobs started, by actor. */
  onStarted: (jobs: Record<string, string>) => void;
}

/** Each actor's voiceover request, or none while one cannot be made. */
export function voiceoversOf(
  model: CatalogAudioModel | undefined,
  casts: readonly Cast[],
  script: string,
): ActorStart<Voiceover>[] | undefined {
  if (model === undefined || casts.length === 0) return undefined;
  const starts: ActorStart<Voiceover>[] = [];
  for (const cast of casts) {
    const voice = voiceOf(cast);
    if (voice === null) return undefined;
    const result = AudioVoiceoverRequest.safeParse({
      type: "audio.voiceover",
      model: model.id,
      prompt: script.trim(),
      voice_id: voice.id,
      ...(cast.stability === null ? {} : { stability: cast.stability }),
    });
    if (!result.success) return undefined;
    starts.push({ actorId: cast.actor.key, data: result.data });
  }
  return starts;
}

/**
 * Live's "Generate Audios" (G8-talking-head, PR A): one `audio.voiceover` job per actor, its
 * voice reading the trimmed script at its stability (none sent leaves a8's 0.7). They are free,
 * as live's, and priced and started as the Talking Head's steps are (`usePricedStarts`).
 */
export function useVoiceovers({ teamId, brandId, model, casts, script, onStarted }: VoiceoversOptions) {
  const step = usePricedStarts({
    teamId,
    brandId,
    starts: voiceoversOf(model, casts, script),
    onStarted: (jobs) =>
      onStarted(Object.fromEntries(jobs.map(({ actorId, job }) => [actorId, job.id]))),
  });
  // Nothing the step's jobs share: a voiceover carries no group.
  return { ...step, start: () => step.start() };
}
